// lib/utils/number.ts
// BIDIX AI - Number Utilities
// Version: 2.2
// Last Updated: 2025-01-28

import { PropertySeed } from "@/lib/core/types";

/**
 * 숫자를 천 단위로 반올림
 * @param value 반올림할 숫자
 * @returns 천 단위로 반올림된 숫자
 * @example
 * roundToK(1234567) // 1235000
 * roundToK(1234) // 1000
 */
export function roundToK(value: number): number {
  return Math.round(value / 1000) * 1000;
}

/**
 * 문자열 시드를 해시하여 0~1 사이의 의사 랜덤 값 생성
 * 같은 시드에서 항상 같은 값을 반환 (시드 기반 일관성 보장)
 * djb2 해시 알고리즘 사용 (더 나은 분산)
 * @param seed 시드 문자열
 * @returns 0~1 사이의 의사 랜덤 값
 */
export function seedBasedRandom(seed: string): number {
  // 빈 문자열 체크
  if (!seed || seed.length === 0) {
    seed = "default_seed";
  }
  
  let hash = 5381; // djb2 해시 초기값
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = ((hash << 5) + hash) + char; // hash * 33 + char
    // 32bit 정수 범위로 제한 (오버플로우 방지)
    hash = hash | 0;
  }
  // 음수 방지 및 0~1 범위로 정규화
  const normalized = Math.abs(hash) / 2147483647;
  // NaN 방지 (0~1 범위 보장)
  return Math.max(0, Math.min(1, isNaN(normalized) ? 0.5 : normalized));
}

/**
 * 시드 기반 정수 난수 생성
 * @param seed 시드 문자열
 * @param min 최소값 (포함)
 * @param max 최대값 (포함)
 * @returns min~max 사이의 정수
 */
export function seedBasedRandomInt(
  seed: string,
  min: number,
  max: number,
): number {
  const random = seedBasedRandom(seed);
  const range = max - min + 1;
  return Math.floor(random * range) + min;
}

/**
 * PropertySeed의 고유 값들을 조합하여 시드 문자열 생성
 * 각 경쟁자별로 고유한 시드를 생성하기 위해 index 파라미터 사용
 * seed.id와 auctionStep을 포함하여 매물별, 유찰 회차별 고유성 보장
 * @param seed PropertySeed 객체
 * @param index 경쟁자 인덱스 (0부터 시작)
 * @returns 시드 문자열
 */
export function generateSeedFromPropertySeed(
  seed: PropertySeed,
  index: number,
): string {
  // PropertySeed의 고유 값들을 조합하여 시드 생성
  // seed.id와 auctionStep을 포함하여 매물별, 유찰 회차별 고유성 보장
  const parts = [
    seed.id || "", // 매물 고유 ID (가장 중요)
    seed.address || "",
    seed.type,
    seed.sizeM2.toString(),
    seed.yearBuilt?.toString() || "0",
    seed.auctionStep?.toString() || "1", // 유찰 회차 포함
    seed.difficulty,
    index.toString(), // 경쟁자 인덱스 (각 경쟁자별 고유성)
  ];
  return parts.join("|");
}

/**
 * 백분율 포맷팅 함수
 * @param value 백분율 값 (예: 0.15는 15%로 표시)
 * @param decimals 소수점 자릿수 (기본값: 2)
 * @returns 포맷된 백분율 문자열 (예: "15.00%")
 * @example
 * formatPercentage(0.1523) // "15.23%"
 * formatPercentage(0.1523, 1) // "15.2%"
 */
export function formatPercentage(value: number, decimals: number = 2): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * 베타 분포 생성 함수 (시드 기반)
 * 베타 분포는 왜도(skewness)를 조절할 수 있는 분포입니다.
 * 
 * @param seed 시드 문자열
 * @param alpha 베타 분포의 alpha 파라미터 (양수)
 * @param beta 베타 분포의 beta 파라미터 (양수)
 * @returns 0~1 사이의 베타 분포 값
 * 
 * @example
 * // 왼쪽 치우침 (낮은 값에 집중): alpha < beta
 * generateBetaDistribution(seed, 2, 5) // 보수적 경쟁자용
 * 
 * // 대칭 분포: alpha = beta
 * generateBetaDistribution(seed, 3, 3) // 중간 경쟁자용
 * 
 * // 오른쪽 치우침 (높은 값에 집중): alpha > beta
 * generateBetaDistribution(seed, 5, 2) // 공격적 경쟁자용
 * 
 * 구현 방법: 두 개의 감마 분포를 사용한 베타 분포 생성
 * (간단한 근사법: 균등 분포의 거듭제곱 조합)
 */
export function generateBetaDistribution(
  seed: string,
  alpha: number,
  beta: number,
): number {
  // 안전성 체크
  const a = Math.max(0.5, alpha);
  const b = Math.max(0.5, beta);
  
  // 두 개의 균등 분포 생성 (시드 기반)
  const u1 = seedBasedRandom(seed + "_beta_u1");
  const u2 = seedBasedRandom(seed + "_beta_u2");
  
  // 0과 1 제외 (로그 계산 안전성)
  const safeU1 = Math.max(0.0001, Math.min(0.9999, u1));
  const safeU2 = Math.max(0.0001, Math.min(0.9999, u2));
  
  // 베타 분포 근사: 두 균등 분포의 거듭제곱 조합
  // X ~ Beta(alpha, beta) ≈ (U1^(1/alpha)) / (U1^(1/alpha) + U2^(1/beta))
  const x1 = Math.pow(safeU1, 1 / a);
  const x2 = Math.pow(safeU2, 1 / b);
  
  // 베타 분포 값 계산 (정규화)
  const betaValue = x1 / (x1 + x2);
  
  // 0~1 범위 보장 및 NaN 방지
  if (isNaN(betaValue) || !isFinite(betaValue)) {
    // NaN 발생 시 alpha/(alpha+beta)를 평균값으로 사용
    return a / (a + b);
  }
  
  return Math.max(0, Math.min(1, betaValue));
}

/**
 * 금액 포맷팅 함수
 * @param value 금액 값
 * @returns 포맷된 금액 문자열 (예: "1,234,567원")
 * @example
 * formatCurrency(1234567) // "1,234,567원"
 */
export function formatCurrency(value: number): string {
  return `${value.toLocaleString()}원`;
}